export type CreditUnit = "chat_conversation" | "voice_minute";
export type CreditPriceSource = "override" | "plan" | "global" | "none";
export type CreditLedgerKind = "topup" | "adjustment" | "chat_debit" | "voice_debit" | "refund";

export const CREDIT_UNITS: CreditUnit[] = ["chat_conversation", "voice_minute"];

export const CREDIT_UNIT_LABEL: Record<CreditUnit, string> = {
  chat_conversation: "Conversación de chat",
  voice_minute: "Minuto de voz",
};

export const CREDIT_UNIT_SUFFIX: Record<CreditUnit, string> = {
  chat_conversation: "por conversación",
  voice_minute: "por minuto",
};

export const CREDIT_PRICE_SOURCE_LABEL: Record<CreditPriceSource, string> = {
  override: "Precio propio de la cuenta",
  plan: "Derivado del plan",
  global: "Tarifa global",
  none: "Sin precio",
};

export const CREDIT_LEDGER_KIND_LABEL: Record<CreditLedgerKind, string> = {
  topup: "Recarga",
  adjustment: "Ajuste",
  chat_debit: "Conversación de chat",
  voice_debit: "Llamada de voz",
  refund: "Devolución",
};

export const MAX_CREDIT_PRICE_COP = 10_000_000;

export interface ResolvedCreditPrice {
  priceCop: number | null;
  source: CreditPriceSource;
}

export function formatCop(amount: number | null | undefined): string {
  if (amount === null || amount === undefined || !Number.isFinite(amount)) return "—";
  return `$${Math.round(amount).toLocaleString("es-CO")}`;
}

export const MIN_FORGIVE_REASON_LENGTH = 10;

export function pendingChargesLabel(count: number): string {
  return `${count.toLocaleString("es-CO")} ${count === 1 ? "cobro pendiente" : "cobros pendientes"}`;
}

export function forgivePendingTitle(count: number, owedCop: number): string {
  return `Condonar ${pendingChargesLabel(count)} por ${formatCop(owedCop)}`;
}

export function parseCreditPriceInput(raw: string): number | "invalid" {
  const cleaned = raw.replace(/[$.\s]/g, "");
  if (!/^\d+$/.test(cleaned)) return "invalid";
  const value = Number(cleaned);
  if (!Number.isSafeInteger(value) || value <= 0 || value > MAX_CREDIT_PRICE_COP) return "invalid";
  return value;
}

function isSource(value: unknown): value is CreditPriceSource {
  return value === "override" || value === "plan" || value === "global" || value === "none";
}

export function parseResolvedPrice(raw: unknown): ResolvedCreditPrice {
  const row = (raw ?? {}) as { price_cop?: unknown; source?: unknown };
  const price = row.price_cop === null || row.price_cop === undefined ? null : Number(row.price_cop);
  const source = isSource(row.source) ? row.source : "none";
  if (source === "none" || price === null || !Number.isFinite(price) || price <= 0) {
    return { priceCop: null, source: "none" };
  }
  return { priceCop: price, source };
}

export function resolvedPriceLabel(unit: CreditUnit, price: ResolvedCreditPrice): string {
  if (price.source === "none" || price.priceCop === null) {
    return unit === "chat_conversation"
      ? "Sin precio: esta cuenta no puede consumir conversaciones de su saldo. Define una tarifa global o un precio propio."
      : "Sin precio: esta cuenta no puede consumir minutos de voz de su saldo. Define una tarifa global o un precio propio.";
  }
  return `${formatCop(price.priceCop)} ${CREDIT_UNIT_SUFFIX[unit]} · ${CREDIT_PRICE_SOURCE_LABEL[price.source]}`;
}

export function pendingReasonLabel(reason: string): string {
  if (reason === "no_price") return "Sin precio";
  if (reason === "insufficient") return "Saldo insuficiente";
  return reason;
}

export function ledgerKindLabel(kind: string): string {
  return CREDIT_LEDGER_KIND_LABEL[kind as CreditLedgerKind] ?? kind;
}

export function priceChangeSummary(before: unknown, after: unknown): string {
  const read = (state: unknown) => {
    const value = (state as { price_cop?: unknown } | null)?.price_cop;
    return value === null || value === undefined ? null : Number(value);
  };
  const from = read(before);
  const to = read(after);
  const label = (v: number | null) => (v === null ? "sin precio" : formatCop(v));
  return `${label(from)} → ${label(to)}`;
}

export function unitOfChange(after: unknown, before: unknown): CreditUnit | null {
  const unit = (after as { unit?: unknown } | null)?.unit ?? (before as { unit?: unknown } | null)?.unit;
  return unit === "chat_conversation" || unit === "voice_minute" ? unit : null;
}

export const CREDITS_TOPUP_PATH = "/app/settings/account#recargar";
